import { arr, bool, f32, obj, str, u8, u16, u32, type Infer } from './fields.js';

/**
 * Versão do protocolo. O servidor aceita esta e a anterior (REV-05 / invariante 8).
 * ALWAYS incremente ao adicionar, remover ou reordenar campo ou mensagem.
 */
export const PROTOCOL_VERSION = 4;
export const MIN_SUPPORTED_VERSION = 1;

/**
 * Fonte única de verdade das mensagens.
 * NEVER edite a ordem de campos sem incrementar PROTOCOL_VERSION:
 * a ordem é o formato do fio, e wire.test.ts falha se ela mudar.
 */
export const Messages = {
  // ---- cliente → servidor ----
  hello: obj({
    protocolVersion: u16,
    roomId: str,
    /** Identidade de convidado. Convidado já é usuário real (REV-12). */
    guestToken: str,
  }),
  input: obj({
    seq: u32,
    /** Tick que o cliente acredita estar simulando. Servidor valida. */
    tick: u32,
    moveX: f32,
    moveZ: f32,
    /** Bitmask de ações. */
    actions: u8,
  }),
  ping: obj({ t: u32 }),

  // ---- servidor → cliente ----
  welcome: obj({
    protocolVersion: u16,
    playerId: u8,
    tickRate: u8,
    seed: u32,
    matchId: str,
    /**
     * As zonas de PERIGO da sala. Uma vez, na entrada — e NEVER no snapshot: elas
     * NEVER mudam durante a partida, e repetir conteúdo estático 15 vezes por
     * segundo, para sempre, é a definição de desperdício de fio.
     *
     * Viajam porque o cliente PREVÊ o próprio avatar: sem conhecer a zona, a
     * predição diverge dentro dela e o jogador sente borracha exatamente onde o
     * jogo mais precisa ser claro. A SAÍDA NEVER viaja, e a diferença é essa —
     * ela não muda a física de ninguém (`ADR-0019`).
     */
    hazards: arr(obj({ x: f32, z: f32, radius: f32, push: f32, maxSpeed: f32 })),
  }),
  snapshot: obj({
    tick: u32,
    /** Último seq de input deste jogador que o servidor aplicou. Base da reconciliação. */
    ackSeq: u32,
    full: bool,
    players: arr(
      obj({
        id: u8,
        x: f32,
        z: f32,
        vx: f32,
        vz: f32,
        /**
         * Velocidade que o CAMPO deu, separada da do direcional no sim porque as
         * duas morrem em ritmos diferentes.
         *
         * Viaja desde `ADR-0017` porque a predição NEVER fecha sem ela: era zerada
         * a cada reconciliação, e o resultado era o cliente perder o impulso do
         * puxão 15 vezes por segundo enquanto o servidor o mantinha. Quem segura o
         * botão sente isso como o robô lutando contra si mesmo.
         */
        ix: f32,
        iz: f32,
        /**
         * Última direção intencional. É por ela que o objeto capturado é lançado,
         * e é ela que gira o avatar na tela — sem isto no fio, o robô de todo mundo
         * aponta para o mesmo lado a partida inteira.
         */
        facingX: f32,
        facingZ: f32,
        hp: u8,
        /**
         * Corpo que ESTE jogador magnetiza agora; 0 = campo desligado.
         *
         * Estado autoritativo, e NEVER enfeite: sem ele o cliente NEVER tem como
         * desenhar o feixe de ninguém em partida em rede, e `feedback essencial`
         * do `first_prototype` deixa de existir fora do solo. É também o que faz
         * `campos combinados` ser LEGÍVEL: dá para ver que o companheiro está
         * puxando o mesmo objeto que você.
         */
        magnetTargetId: u8,
      }),
    ),
    /**
     * Os corpos da sala. Terceira categoria de objeto em rede, e NEVER uma lista
     * a mais de jogadores — ver `ADR-0017`.
     *
     * Corpo FIXO também viaja, e isso é de propósito: a âncora NEVER se move, mas
     * é ela que decide para onde o robô é puxado, e um cliente que não sabe onde
     * ela está NEVER pode prever o próprio avatar. O custo é ~14 bytes por corpo
     * parado, contra o cliente ter que adivinhar o conteúdo da sala.
     */
    bodies: arr(
      obj({
        id: u8,
        /**
         * Código do tipo, de `bodyKindCode` em `@game/sim`. String no fio seria o
         * nome do tipo repetido a cada snapshot, a 15 Hz, para sempre.
         *
         * A numeração é a MESMA do hash de estado, e é de propósito: ela já existe,
         * já é estável e já é proibido renumerar. Uma segunda tabela seria uma
         * segunda chance de as duas discordarem.
         */
        kind: u8,
        x: f32,
        z: f32,
        vx: f32,
        vz: f32,
        /** Jogador que capturou; 0 = ninguém. */
        heldBy: u8,
        /**
         * Quantos campos magnéticos estão puxando ESTE corpo agora.
         *
         * É o `autoridade explícita sobre objetos disputados` do PULSO, no fio: é
         * por este número que o cliente sabe se a própria predição vale. Um só
         * campo e o dono pode prever; dois ou mais e a força é a SOMA, que o
         * cliente NEVER conhece — então ele para de prever e aceita o servidor.
         *
         * DERIVADO a cada snapshot, e NEVER guardado no estado do sim: guardar
         * mudaria o hash e invalidaria o corpus de replay inteiro por um número
         * que a sala sabe calcular varrendo quatro jogadores.
         */
        claims: u8,
      }),
    ),
  }),
  reject: obj({ code: u8, message: str }),
  pong: obj({ t: u32 }),
} as const;

/**
 * Ordem no fio, explícita de propósito.
 * NEVER reordene: o índice nesta lista É o id da mensagem no byte 0.
 */
export const MESSAGE_ORDER = [
  'hello',
  'input',
  'ping',
  'welcome',
  'snapshot',
  'reject',
  'pong',
] as const;

export type MessageName = keyof typeof Messages;
export type MessageData<N extends MessageName> = Infer<(typeof Messages)[N]>;
export type AnyMessage = {
  [N in MessageName]: { readonly type: N; readonly data: MessageData<N> };
}[MessageName];

/** Checagem em tempo de compilação: MESSAGE_ORDER cobre exatamente as chaves de Messages. */
type AssertNever<T extends never> = T;
export type _NoMissingInOrder = AssertNever<
  Exclude<MessageName, (typeof MESSAGE_ORDER)[number]>
>;
export type _NoExtraInOrder = AssertNever<
  Exclude<(typeof MESSAGE_ORDER)[number], MessageName>
>;

/**
 * Códigos de recusa. Acrescentar código NEVER muda o formato do fio — `code` é u8 —
 * então NEVER exige subir `PROTOCOL_VERSION`. Cliente antigo mostra o código que
 * não conhece; NEVER quebra.
 */
export const REJECT_CODES = {
  protocolTooOld: 1,
  protocolTooNew: 2,
  roomFull: 3,
  badInput: 4,
  rateLimited: 5,
  /** A partida chegou ao fim. Placar e revanche são gameplay, decididos na fase 02. */
  matchOver: 6,
  /**
   * A sala quebrou e foi destruída. O cliente NEVER tem o que consertar: reconectar
   * cai numa sala nova. Existe separado de `matchOver` porque a partida NEVER chegou
   * ao fim — dizer que chegou seria mentir para quem está do outro lado.
   */
  roomFaulted: 7,
  /**
   * O Núcleo foi extraído: a partida acabou em VITÓRIA.
   *
   * Existe separado de `matchOver` porque as duas frases que o jogador lê são
   * opostas — "o tempo acabou" e "vocês conseguiram" —, e quem decide o texto é o
   * CÓDIGO, e NEVER a string de motivo (`ui/match-end.ts`). Cliente antigo que
   * NEVER conhece o 8 cai no caso de rede e oferece jogar de novo, que continua
   * sendo a coisa certa a oferecer.
   */
  matchWon: 8,
} as const;
